import { useField } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';

import { ErrorMessage, Skeleton } from '@components';
import type { BaseSelectInputProps } from '@types';
import { cn } from '@utils';

export const BaseSelectInput: React.FC<BaseSelectInputProps> = ({
  disabled,
  name,
  errorClassName = '',
  options,
  loading = false,
  placeholder = '',
  className = '',
  format,
  value,
  onChange,
  customWidth,
  label = '',
  allowEmptyValue = false,
}: BaseSelectInputProps) => {
  const [field, meta, helpers] = useField(name);
  const isError = meta.error && meta.touched;

  const dynamicClasses = cn(
    'block flex rounded-md hover:cursor-pointer',
    'text-black text-us text-ellipsis placeholder-gray-400',
    'w-full pl-2.5 pr-8 py-1.5',
    'border-0 ring-1 ring-theme-border-default focus:ring-2 focus:ring-indigo-600',
    isError && 'ring-theme-error-main bg-red-500/10 focus:ring-theme-error-main',
    disabled && 'bg-gray-200/30 hover:cursor-not-allowed text-gray-400/50',
    className,
  );

  const dynamicSkeletonClass = cn('h-10 rounded-md w-full', customWidth);

  if (loading) return <Skeleton className={dynamicSkeletonClass} />;

  return (
    <div className={cn('flex flex-col h-fit space-y-3 w-full', customWidth)}>
      <div className="relative group flex items-center">
        <select
          {...field}
          id={name}
          name={name}
          aria-invalid={!!(meta.error && meta.touched)}
          value={value ?? field.value ?? ''}
          disabled={disabled}
          aria-describedby={isError ? `${name}-error` : undefined}
          className={dynamicClasses}
          onChange={onChange ?? ((e) => helpers.setValue(e.target.value))}
        >
          {placeholder && (
            <option disabled value="">
              {placeholder}
            </option>
          )}
          {options?.map((option) => {
            if (!allowEmptyValue && (option.value === '' || option.value === undefined)) {
              return null;
            }

            return (
              <option key={option.value} value={option.value} disabled={option.disabled} className="truncate">
                {format ? format(`${option.label}`) : option.label}
              </option>
            );
          })}
        </select>
        {label && label}
      </div>
      <AnimatePresence>
        {isError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ErrorMessage isError={isError} errorClassName={errorClassName} meta={meta} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
